import { ChangeEvent, useEffect, useState } from 'react';
import { FieldValues, useForm } from 'react-hook-form';
import {
  SdpGetFullMrMonth,
  SdpResponse,
  headPointNotCapable,
  headPointPhysicalMeter,
} from '../../../models/SdpResponse';
import { UIStateProps, uiState } from '../../../atoms/uiAtom';
import { useGetConventionAvailability, useUpdateConvention } from '../../../hooks/meters';
import { useGetIsAccMember, useUpdateSdp } from '../../../hooks/sdps';
import { useRecoilState, useSetRecoilState } from 'recoil';

import Button from '../../common/Button';
import { ConsumptionUnitEnum } from '../../../enum/ConsumptionUnitEnum';
import { HeadPointGetAddress } from '../../../models/HeadPointResponse';
import Input from '../../forms/Input';
import InputSwitch from '../../forms/InputSwitch';
import Loader from '../../common/Loader';
import { MeterTypeEnum } from '../../../enum/MeterTypeEnum';
import ModalExportSdpData from '../../modals/ModalExportSdpData';
import ModalInformation from '../../modals/ModalInformation';
import ModalMeterConsentChange from '../../modals/ModalMeterConsentChange';
import { ModalResponse } from '../../../enum/ModalResponse';
import { ReactComponent as PencilIcon } from '../../../assets/pencil.svg';
import { SDPDivisionEnum } from '../../../enum/SDPDivisionEnum';
import { SDPReadingConsentEnum } from '../../../enum/SDPReadingConsentEnum';
import { SDPReadingNicheEnum } from '../../../enum/SDPReadingNicheEnum';
import { SDPTariffChoiceEnum } from '../../../enum/SDPTariffChoiceEnum';
import { SDPTimeFrameEnum } from '../../../enum/SDPTimeFrameEnum';
import SectionList from '../../common/sectionList/SectionList';
import SectionListItem from '../../common/sectionList/SectionListItem';
import Skeleton from 'react-loading-skeleton';
import classNames from 'classnames';
import { roleAccessState } from '../../../atoms/roleAccessAtom';
import { sdpState } from '../../../atoms/sdpAtom';
import { useGetRolesAccess } from '../../../hooks/auth';
import { useTranslation } from 'react-i18next';
import { userPreferencesState } from '../../../atoms/userPreferencesAtom/userPreferencesAtom';

export interface MeterSettingsProps {
  sdp?: SdpResponse | null;
}
export default function MeterSettings({ sdp }: MeterSettingsProps) {
  const { t, i18n } = useTranslation();
  const [roleAccess, setRoleAccess] = useRecoilState(roleAccessState);
  const { refetch: refetchRoleAccess } = useGetRolesAccess(sdp?.id);
  const [isOpenModalMeterConsentChange, setIsOpenModalMeterConsentChange] = useState(false);
  const [isOpenModalExportSdpData, setIsOpenModalExportSdpData] = useState(false);

  const [isOpenModalMeterNotCapableInformation, setIsOpenModalMeterNotCapableInformation] =
    useState(false);

  const { isFetching: isFetchingIsAccMember, data: isAccMember } = useGetIsAccMember(sdp?.id);

  const [hasMeterNameLoader, setHasMeterNameLoader] = useState(false);
  const [hasTariffLoader, setHasTariffLoader] = useState(false);

  const [readingConsentValue, setReadingConsentValue] = useState(false);

  const { mutateAsync: updateMeter } = useUpdateSdp();
  const setUiState = useSetRecoilState<UIStateProps>(uiState);

  const {
    isFetching: isFetchingConventionAvailability,
    data: conventionAvailability,
    refetch: fetchConventionAvailability,
  } = useGetConventionAvailability(sdp?.id);

  const { isPending: isPendingUpdateConvention, mutateAsync: mutateAsyncUpdateConvention } =
    useUpdateConvention();

  const [isOpenModalRemoteReadInformation, setIsOpenModalRemoteReadInformation] = useState(false);

  const [isOpenModalTariffInformation, setIsOpenModalTariffInformation] = useState(false);

  const setUserPreferences = useSetRecoilState(userPreferencesState);
  const setSdpState = useSetRecoilState(sdpState);
  const {
    register,
    handleSubmit,
    setFocus,
    setValue,
    formState: { errors },
  } = useForm<FieldValues>({
    defaultValues: {
      meterName: sdp?.userHasAccessNickname,
    },
  });

  useEffect(() => {
    setValue('meterName', sdp?.userHasAccessNickname);
  }, [sdp?.userHasAccessNickname, setValue]);

  useEffect(() => {
    if (
      sdp &&
      headPointNotCapable(sdp?.headpoint!) &&
      roleAccess?.settings.hasWarningCapable &&
      sdp.division !== SDPDivisionEnum.GAS
    ) {
      setIsOpenModalMeterNotCapableInformation(true);
    }
  }, [roleAccess?.settings.hasWarningCapable, sdp]);

  useEffect(() => {
    if (sdp?.id && roleAccess?.settings.hasFieldConsent) {
      fetchConventionAvailability().then((conventionAvailability) => {
        if (conventionAvailability.error) {
          setUiState((prev) => ({ ...prev, hasError: true }));
        }
      });
    }
  }, [fetchConventionAvailability, roleAccess?.settings.hasFieldConsent, sdp?.id, setUiState]);

  const handleClickInfoRemoteRead = () => {
    setIsOpenModalRemoteReadInformation(true);
  };

  const handleClickActionRemoteReadInformation = () => {
    setIsOpenModalRemoteReadInformation(false);
  };

  const handleClickInfoTariff = () => {
    setIsOpenModalTariffInformation(true);
  };

  const handleClickActionTariffInformation = () => {
    setIsOpenModalTariffInformation(false);
  };

  const handleOnchangeTariff = (value: boolean | ChangeEvent<HTMLInputElement>) => {
    const updatedSdp = {
      ...sdp,
      tariffChoice: value ? SDPTariffChoiceEnum.SOCIAL : SDPTariffChoiceEnum.NONE,
    } as SdpResponse;
    setHasTariffLoader(true);
    updateMeter({
      id: updatedSdp.id,
      tariffChoice: updatedSdp.tariffChoice,
    })
      .then(() => {
        setSdpState((state) => ({
          ...state,
          items: state.items?.map((item) => (item.id === updatedSdp.id ? updatedSdp : item)),
        }));
        refetchRoleAccess()
          .then((response) => {
            if (response.error) {
              setUiState((prev) => ({ ...prev, hasError: true }));
            } else {
              setRoleAccess(() => response.data?.data!);
              setUserPreferences((state) => ({
                ...state,
                unit: value ? ConsumptionUnitEnum.EURO : ConsumptionUnitEnum.KWH,
              }));
            }
          })
          .catch(() => {
            setUiState((prev) => ({ ...prev, hasError: true }));
          });
      })
      .catch(() => {
        setUiState((prev) => ({ ...prev, hasError: true }));
      })
      .finally(() => {
        setHasTariffLoader(false);
      });
  };

  const handleOnchangeRemoteReading = (value: boolean | ChangeEvent<HTMLInputElement>) => {
    setReadingConsentValue(value as boolean);
    if (
      conventionAvailability?.data.readingConsent === SDPReadingConsentEnum.YES &&
      value === false
    ) {
      setIsOpenModalMeterConsentChange(true);
    } else {
      mutateAsyncUpdateConvention({
        sdpId: sdp!.id,
        hasReadingConsent: value as boolean,
      })
        .catch(() => {
          setUiState((prev) => ({ ...prev, hasError: true }));
        })
        .finally(() => {
          fetchConventionAvailability();
        });
    }
  };

  const hasRemoteReadingAvailable =
    roleAccess?.settings.isConsentClickable &&
    conventionAvailability?.data.readingConsent === SDPReadingConsentEnum.YES &&
    isAccMember?.status === 204 &&
    isFetchingIsAccMember === false;

  const handleClickActionModalConsentChange = (response?: ModalResponse) => {
    setIsOpenModalMeterConsentChange(false);
    if (response === ModalResponse.OK) {
      mutateAsyncUpdateConvention({
        sdpId: sdp!.id,
        hasReadingConsent: readingConsentValue,
      })
        .catch(() => {
          setUiState((prev) => ({ ...prev, hasError: true }));
        })
        .finally(() => {
          fetchConventionAvailability();
        });
    }
  };

  const [isEditableMeterName, setIsEditableMeterName] = useState(false);

  const handleClickEditMeterName = () => {
    setIsEditableMeterName((prevState) => !prevState);
    setTimeout(() => {
      setFocus('meterName');
    }, 500);
  };

  const onSubmit = (data: FieldValues) => {
    const updatedSdp = {
      ...sdp,
      userHasAccessNickname: data.meterName,
    } as SdpResponse;
    setHasMeterNameLoader(true);
    updateMeter({
      id: updatedSdp.id,
      userHasAccessNickname: updatedSdp.userHasAccessNickname,
    })
      .then(() => {
        setSdpState((state) => ({
          ...state,
          items: state.items?.map((item) => (item.id === updatedSdp.id ? updatedSdp : item)),
        }));
        setIsEditableMeterName((prevState) => !prevState);
        refetchRoleAccess()
          .then((response) => {
            if (response.error) {
              setUiState((prev) => ({ ...prev, hasError: true }));
            } else {
              setRoleAccess(() => response.data?.data!);
            }
          })
          .catch(() => {
            setUiState((prev) => ({ ...prev, hasError: true }));
          });
      })
      .catch(() => {
        setUiState((prev) => ({ ...prev, hasError: true }));
      })
      .finally(() => {
        setHasMeterNameLoader(false);
      });
  };

  const handleClickActionModalExportSdpData = () => {
    setIsOpenModalExportSdpData(!isOpenModalExportSdpData);
  };

  const handleClickActionModalNotCapableInformation = () => {
    setIsOpenModalMeterNotCapableInformation(!isOpenModalMeterNotCapableInformation);
  };

  const handleClickExportSdpData = () => {
    setIsOpenModalExportSdpData(true);
  };

  return (
    <div className="meter-settings">
      {roleAccess?.myConsumption.isAvailable && (
        <div className="meter-settings__actions d-flex  justify-content-end mb-4 mt-4">
          {sdp ? (
            <Button
              className="btn btn--primary meter-settings__actions__export-btn"
              onClick={handleClickExportSdpData}
            >
              {t('overview.meter.detail.export.data')}
            </Button>
          ) : (
            <Skeleton
              containerClassName="meter-settings__actions__skeleton-export-btn"
              count={1}
              height={36}
            />
          )}
        </div>
      )}
      <div className="widget">
        <SectionList title={t('overview.meter.detail.information.general')} isFetching={!sdp}>
          <SectionListItem
            title={t('overview.meter.detail.information.name')}
            value={
              <form className="form" onSubmit={handleSubmit(onSubmit)}>
                <Input
                  name="meterName"
                  className={classNames('input--full-width', {
                    'input--focus': isEditableMeterName,
                  })}
                  defaultValue={sdp?.userHasAccessNickname}
                  register={register}
                  isDisabled={!isEditableMeterName}
                  error={
                    errors.meterName?.type === 'maxLength'
                      ? t('step.customize.meter.name.input.error.maxLength')
                      : undefined
                  }
                  validations={{ maxLength: 20 }}
                  suffix={
                    hasMeterNameLoader ? (
                      <Loader color="#a3117b" />
                    ) : !isEditableMeterName ? (
                      <PencilIcon
                        className="icon icon--small icon--action icon--primary"
                        onClick={handleClickEditMeterName}
                      />
                    ) : (
                      <Button type="submit">
                        <span className="icon--primary">{t('common.terminate')}</span>
                      </Button>
                    )
                  }
                />
              </form>
            }
            isFetching={!sdp}
          />
          {roleAccess?.settings.hasFieldConsent && (
            <SectionListItem
              title={t('overview.meter.detail.information.remote.read')}
              isFetching={!sdp}
              caption={
                conventionAvailability?.data.readingConsent === SDPReadingConsentEnum.NO
                  ? t('overview.meter.detail.information.remote.read.caption')
                  : undefined
              }
              trailing={
                <div className="d-flex align-items-center">
                  <InputSwitch
                    name="remote-reading"
                    className="input--with-info"
                    value={
                      sdp?.readingConsent === SDPReadingConsentEnum.YES ||
                      sdp?.readingNiche === SDPReadingNicheEnum.MANDATORY
                    }
                    isDisabled={
                      isFetchingConventionAvailability ||
                      isPendingUpdateConvention ||
                      hasRemoteReadingAvailable === false
                    }
                    onChange={handleOnchangeRemoteReading}
                  />
                  {(isFetchingConventionAvailability || isPendingUpdateConvention) && (
                    <div className="mb-1">
                      <Loader color="#a3117b" />
                    </div>
                  )}
                </div>
              }
              onClickInfo={handleClickInfoRemoteRead}
            />
          )}
          {roleAccess?.settings.hasToggleSocial && (
            <SectionListItem
              title={t('overview.meter.detail.information.tariff')}
              trailing={
                <div className="d-flex align-items-center">
                  <InputSwitch
                    name="tariff"
                    className="input--with-info"
                    isDisabled={hasTariffLoader}
                    value={sdp?.tariffChoice === SDPTariffChoiceEnum.SOCIAL}
                    onChange={handleOnchangeTariff}
                  />

                  {hasTariffLoader && (
                    <div className="mb-1">
                      <Loader color="#a3117b" />
                    </div>
                  )}
                </div>
              }
              isFetching={!sdp}
              onClickInfo={handleClickInfoTariff}
            />
          )}
        </SectionList>

        <SectionList title={t('overview.meter.detail.information.general.info')} isFetching={!sdp}>
          <SectionListItem
            title={t('overview.meter.detail.information.address')}
            value={HeadPointGetAddress(i18n.language, sdp?.headpoint)}
            isFetching={!sdp}
          />
          {roleAccess?.settings.hasFieldContractOwner && (
            <SectionListItem
              title={t('overview.meter.detail.information.contract.owner')}
              value={sdp?.netUserName}
              isFetching={!sdp}
            />
          )}
          {roleAccess?.settings.hasFieldNextMeterReading && (
            <SectionListItem
              title={t('overview.meter.detail.information.next.meter.reading')}
              value={SdpGetFullMrMonth(i18n.language, t, sdp)}
              isFetching={!sdp}
            />
          )}
        </SectionList>

        <SectionList title={t('overview.meter.detail.information.meter')} isFetching={!sdp}>
          <SectionListItem
            title={t('overview.meter.detail.information.code.ean')}
            value={sdp?.headpoint.ean}
            isFetching={!sdp}
          />
          {roleAccess?.settings.hasFieldMeterSerial && (
            <SectionListItem
              title={t('overview.meter.detail.information.sn.meter')}
              value={headPointPhysicalMeter(sdp?.headpoint)?.serialNumber}
              isFetching={!sdp}
            />
          )}
          <SectionListItem
            title={t('overview.meter.detail.information.energy')}
            value={t(SDPDivisionEnum.getTranslation(sdp?.division))}
            isFetching={!sdp}
          />
          {roleAccess?.settings.hasFieldTariffType && (
            <SectionListItem
              title={t('overview.meter.detail.information.tariff.type')}
              value={t(SDPTimeFrameEnum.getTranslation(sdp?.timeFrame))}
              isFetching={!sdp}
            />
          )}
          {roleAccess?.settings.hasFieldSmartMeter && (
            <SectionListItem
              title={t('overview.meter.detail.information.ia.meter')}
              value={t(MeterTypeEnum.getTranslation(headPointPhysicalMeter(sdp?.headpoint)?.type))}
              isFetching={!sdp}
            />
          )}
          {roleAccess?.settings.hasFieldSupplier && (
            <SectionListItem
              title={t('overview.meter.detail.information.provider')}
              value={sdp?.supplier}
              isFetching={!sdp}
            />
          )}
        </SectionList>

        <ModalInformation
          isOpen={isOpenModalRemoteReadInformation}
          onClickAction={handleClickActionRemoteReadInformation}
        >
          <p>{t('overview.meter.detail.information.remote.read.info')}</p>
        </ModalInformation>

        <ModalInformation
          isOpen={isOpenModalTariffInformation}
          onClickAction={handleClickActionTariffInformation}
        >
          <p>{t('overview.meter.detail.information.tariff.info')}</p>
        </ModalInformation>

        <ModalMeterConsentChange
          isOpen={isOpenModalMeterConsentChange}
          onClickAction={handleClickActionModalConsentChange}
        />
        <ModalExportSdpData
          isOpen={isOpenModalExportSdpData}
          sdpId={sdp?.id}
          onClickAction={handleClickActionModalExportSdpData}
        />
        <ModalInformation
          isOpen={isOpenModalMeterNotCapableInformation}
          onClickAction={handleClickActionModalNotCapableInformation}
        >
          <h2>{t('modal.meter.not.capable.title')}</h2>
          <p>{t('modal.meter.not.capable.one')}</p>
        </ModalInformation>
      </div>
    </div>
  );
}
